import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Loader2, AlertCircle } from "lucide-react";
import { SITE } from "@/lib/site";
import { useAdminAuth } from "@/hooks/useAdminAuth";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState({ loading: false, error: "" });
  const navigate = useNavigate();
  const { status, login } = useAdminAuth();

  useEffect(() => {
    if (status === "authed") navigate("/admin");
  }, [status, navigate]);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "" });
    try {
      await login({ username, password });
      navigate("/admin");
    } catch (err) {
      setState({ loading: false, error: err?.response?.data?.detail || "Invalid credentials" });
    }
  }, [login, navigate, username, password]);

  return (
    <>
      <SEO title="Admin Login" noIndex />
      <section className="min-h-[calc(100vh-160px)] flex items-center justify-center bg-br-black p-6">
        <div className="max-w-md w-full bg-br-charcoal border border-br-line rounded-lg p-7 text-white">
          <div className="flex flex-col items-center text-center mb-5">
            <img src={SITE.logo} alt="Black Ridge Construction" className="h-20 w-auto object-contain mb-3" />
            <div className="text-[10px] tracking-[0.3em] text-br-bronze-2">ADMIN DASHBOARD</div>
          </div>
          <form onSubmit={submit} className="space-y-3" data-testid="admin-login-form">
            <div>
              <label className="label-br text-gray-300">Username</label>
              <input className="input-br bg-br-black text-white border-br-line" value={username} onChange={(e)=>setUsername(e.target.value)} required data-testid="admin-username" />
            </div>
            <div>
              <label className="label-br text-gray-300">Password</label>
              <input type="password" className="input-br bg-br-black text-white border-br-line" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="admin-password" />
            </div>
            {state.error && <div className="flex items-center gap-2 text-sm text-red-400"><AlertCircle size={14}/> {state.error}</div>}
            <button type="submit" disabled={state.loading} className="br-btn-primary w-full justify-center" data-testid="admin-login-submit">
              {state.loading ? <Loader2 className="animate-spin" size={14}/> : null}
              {state.loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <div className="mt-5 text-xs text-gray-400">Sessions use a secure httpOnly cookie. Tokens are never stored in browser-accessible storage.</div>
          <Link to="/" className="mt-5 inline-block text-xs uppercase tracking-widest text-br-bronze-2 hover:underline">← Back to site</Link>
        </div>
      </section>
    </>
  );
}
